import { SHOP_UPGRADES } from "../data/upgrades"
import type { Player, ShopOffer, Upgrade } from "../types"
import { isUpgradeAvailableForSource } from "../utils"

const SHOP_LIMITS = {
  maxOffers: 3,
  minimumPrice: 5,
  priceWaveScalar: 0.1,
  rerollIncreaseScalar: 0.6,
  rerollBaseWaveScalar: 0.8,
} as const

const SHOP_RARITY_RULES: Record<
  Upgrade["rarity"],
  {
    minWave: number
    baseChance: number
    chancePerWave: number
    maxChance: number
  }
> = {
  common: { minWave: 1, baseChance: 100, chancePerWave: 0, maxChance: 100 },
  uncommon: { minWave: 2, baseChance: 0, chancePerWave: 6, maxChance: 60 },
  rare: { minWave: 4, baseChance: 0, chancePerWave: 2, maxChance: 25 },
  legendary: { minWave: 8, baseChance: 0, chancePerWave: 0.23, maxChance: 8 },
}

const RARITY_BASE_COST: Record<Upgrade["rarity"], number> = {
  common: 28,
  uncommon: 52,
  rare: 95,
  legendary: 180,
}

const BUMPER_PRICE_BONUS = 1

const SHOP_RARITY_ORDER: Upgrade["rarity"][] = [
  "common",
  "uncommon",
  "rare",
  "legendary",
]

interface CreateShopOffersParams {
  wave: number
  player: Player
  lockedOffers?: ShopOffer[]
  blockedUpgradeIds?: string[]
}

// ── Helpers ───────────────────────────────────────────────────

const supportsShopSource = (upgrade: Upgrade): boolean => {
  return (
    !upgrade.offerSource ||
    upgrade.offerSource === "shop" ||
    upgrade.offerSource === "both"
  )
}

const isRarityUnlocked = (wave: number, rarity: Upgrade["rarity"]): boolean => {
  return wave >= SHOP_RARITY_RULES[rarity].minWave
}

const getLuckMultiplier = (luck: number): number => {
  return 1 + Math.max(0, luck) / 100
}

const getCumulativeRarityChance = (
  wave: number,
  rarity: Upgrade["rarity"],
  luck: number,
): number => {
  const rule = SHOP_RARITY_RULES[rarity]
  if (!isRarityUnlocked(wave, rarity)) return 0
  if (rarity === "common") return 100

  const wavesSinceUnlock = Math.max(0, wave - rule.minWave + 1)
  const rawChance = rule.baseChance + rule.chancePerWave * wavesSinceUnlock
  return Math.min(rule.maxChance, rawChance * getLuckMultiplier(luck))
}

const getSpecificRarityWeights = (
  wave: number,
  luck: number,
): Record<Upgrade["rarity"], number> => {
  const legendaryChance = getCumulativeRarityChance(wave, "legendary", luck)
  const rareChance = getCumulativeRarityChance(wave, "rare", luck)
  const uncommonChance = getCumulativeRarityChance(wave, "uncommon", luck)

  return {
    legendary: Math.max(0, legendaryChance),
    rare: Math.max(0, rareChance - legendaryChance),
    uncommon: Math.max(0, uncommonChance - rareChance),
    common: Math.max(0, 100 - uncommonChance),
  }
}

const pickRandomUpgrade = (pool: Upgrade[]): Upgrade | null => {
  if (pool.length === 0) return null
  return pool[Math.floor(Math.random() * pool.length)] ?? null
}

const pickRarityForPool = (
  pool: Upgrade[],
  wave: number,
  luck: number,
): Upgrade["rarity"] | null => {
  const rarityWeights = getSpecificRarityWeights(wave, luck)
  const availableRarities = SHOP_RARITY_ORDER.filter(
    (rarity) =>
      pool.some((u) => u.rarity === rarity) && rarityWeights[rarity] > 0,
  )

  if (availableRarities.length === 0) return pool[0]?.rarity ?? null

  const totalWeight = availableRarities.reduce(
    (sum, r) => sum + rarityWeights[r],
    0,
  )
  if (totalWeight <= 0) return availableRarities[0] ?? null

  let roll = Math.random() * totalWeight
  for (const rarity of availableRarities) {
    roll -= rarityWeights[rarity]
    if (roll <= 0) return rarity
  }

  return availableRarities[availableRarities.length - 1] ?? null
}

const pickShopUpgrade = (
  pool: Upgrade[],
  _player: Player,
  wave: number,
): Upgrade | null => {
  if (pool.length === 0) return null

  const selectedRarity = pickRarityForPool(pool, wave, 1)
  const rarityPool = selectedRarity
    ? pool.filter((u) => u.rarity === selectedRarity)
    : pool

  return pickRandomUpgrade(rarityPool) ?? pickRandomUpgrade(pool)
}

// ── Exported price helpers ────────────────────────────────────

export const getUpgradeBaseCost = (upgrade: Upgrade): number => {
  if (upgrade.type === "item" && upgrade.baseCost) return upgrade.baseCost

  const baseCost = RARITY_BASE_COST[upgrade.rarity]
  const bumperBonus =
    upgrade.type === "item" && upgrade.bumperWeightBoost
      ? BUMPER_PRICE_BONUS
      : 0

  return baseCost + bumperBonus
}

const getShopPrice = (upgrade: Upgrade, wave: number): number => {
  const basePrice = getUpgradeBaseCost(upgrade)
  const completedWaves = Math.max(0, wave - 1)
  const waveTax = Math.floor(completedWaves * 1.5)

  return Math.max(
    SHOP_LIMITS.minimumPrice,
    basePrice +
      waveTax +
      Math.floor(basePrice * SHOP_LIMITS.priceWaveScalar * completedWaves),
  )
}

export const getUpgradeSellPrice = (upgrade: Upgrade): number => {
  return Math.max(
    SHOP_LIMITS.minimumPrice,
    Math.floor(getUpgradeBaseCost(upgrade) / 2),
  )
}

const getShopRerollIncrease = (wave: number): number => {
  return Math.max(
    SHOP_LIMITS.minimumPrice,
    Math.floor(SHOP_LIMITS.rerollIncreaseScalar * wave),
  )
}

export const getShopRerollPrice = (
  wave: number,
  rerollCount: number,
): number => {
  const increase = getShopRerollIncrease(wave)
  const firstReroll =
    1 + Math.floor(Math.max(0, wave - 1) * SHOP_LIMITS.rerollBaseWaveScalar)
  return firstReroll + rerollCount * increase
}

// ── Main shop offer generator ─────────────────────────────────

export const createShopOffers = ({
  wave,
  player,
  lockedOffers = [],
  blockedUpgradeIds = [],
}: CreateShopOffersParams): ShopOffer[] => {
  const normalizedLockedOffers = lockedOffers
    .slice(0, SHOP_LIMITS.maxOffers)
    .map((offer) => ({ ...offer, locked: true }))
  const blockedIds = new Set([
    ...blockedUpgradeIds,
    ...normalizedLockedOffers.map((o) => o.upgrade.id),
  ])

  // Add non-repeatable items the player already owns to blockedIds
  for (const ownedUpgrade of player.items) {
    if (!ownedUpgrade.repeatable) {
      blockedIds.add(ownedUpgrade.id)
    }
  }

  const availableUpgrades = SHOP_UPGRADES.filter(
    (u) =>
      !blockedIds.has(u.id) &&
      supportsShopSource(u) &&
      isRarityUnlocked(wave, u.rarity) &&
      isUpgradeAvailableForSource(player, u, "shop"),
  )

  const offers: ShopOffer[] = [...normalizedLockedOffers]
  const remainingSlots = SHOP_LIMITS.maxOffers - offers.length

  for (let i = 0; i < remainingSlots; i++) {
    const pickedUpgrade = pickShopUpgrade(availableUpgrades, player, wave)

    if (!pickedUpgrade) continue

    blockedIds.add(pickedUpgrade.id)
    const pickedIndex = availableUpgrades.findIndex(
      (u) => u.id === pickedUpgrade.id,
    )
    if (pickedIndex >= 0) availableUpgrades.splice(pickedIndex, 1)

    const basePrice = getShopPrice(pickedUpgrade, wave)
    offers.push({
      slotId: `${wave}-${offers.length}-${pickedUpgrade.id}`,
      upgrade: pickedUpgrade,
      price: Math.max(SHOP_LIMITS.minimumPrice, basePrice),
      locked: false,
    })
  }

  return offers.slice(0, SHOP_LIMITS.maxOffers)
}
